import { Pencil, Trash2 } from "lucide-react";

import { formatDisplayDate } from "../../utils/Date";
import { getCauseCourtRoomLabel } from "./causeCourtLabels";
import Pagination from "../../utils/Pagination";

interface CauseTableProps {
  causes: any[];
  currentPage: number;
  totalPages: number;
  total: number;
  startIndex: number;
  onPageChange: (page: number) => void;
  onEdit: (cause: any) => void;
  onDelete: (id: string) => void;
}

const CauseTable = ({
  causes,
  currentPage,
  totalPages,
  total,
  startIndex,
  onPageChange,
  onEdit,
  onDelete,
}: CauseTableProps) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-gray-200">
        <h2 className="text-base font-semibold text-gray-900">Cases</h2>
        <p className="text-sm text-gray-500">
          Showing {causes.length ? startIndex + 1 : 0}-
          {Math.min(startIndex + causes.length, total)} of {total}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-600 uppercase text-xs tracking-wider">
            <tr>
              <th className="px-4 py-3">Sr. No.</th>
              <th className="px-4 py-3">CNR</th>
              <th className="px-4 py-3">Case No.</th>
              <th className="px-4 py-3">Parties</th>
              <th className="px-4 py-3">Court</th>
              <th className="px-4 py-3">Court Room</th>
              <th className="px-4 py-3">Stage</th>
              <th className="px-4 py-3">Hearing Date</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {causes.map((cause, index) => (
              <tr
                key={cause._id || cause.id}
                className="hover:bg-gray-50 transition-colors"
              >
                <td className="px-4 py-3 text-gray-600">
                  {startIndex + index + 1}
                </td>
                <td className="px-4 py-3 text-gray-700 ">
                  {cause?.cnr || "N/A"}
                </td>
                <td className="px-4 py-3 text-gray-700 ">
                  {cause?.caseNumber || "N/A"}
                </td>
                <td className="px-4 py-3 min-w-[220px]">
                  <p className="font-medium text-gray-900 break-words">
                    {cause?.parties?.length > 0
                      ? cause.parties.find(
                          (party: any) => party?.type === "petitioner",
                        )?.name || "N/A"
                      : cause?.petitioner || "N/A"}
                  </p>

                  <p className="text-gray-500 break-words">
                    vs{" "}
                    {cause?.parties?.length > 0
                      ? cause.parties.find(
                          (party: any) => party?.type === "respondent",
                        )?.name || "N/A"
                      : cause?.respondent || "N/A"}
                  </p>
                </td>
                <td className="px-4 py-3 text-gray-700 ">
                  {cause?.courtType || "N/A"}
                </td>
                <td className="px-4 py-3 text-gray-700 ">
                  {getCauseCourtRoomLabel(cause)}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${getCaseStageColor(
                      cause?.caseStage,
                    )}`}
                  >
                    {cause?.caseStage || "N/A"}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                  {formatDisplayDate(cause?.hearingDate)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                      title="Edit"
                      onClick={() => onEdit(cause)}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                      title="Delete"
                      onClick={() => onDelete(cause._id || cause.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 pb-4 my-5">
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
};

const getCaseStageColor = (stage: string) => {
  const colors = {
    Filing: "bg-blue-100 text-blue-800 border-blue-200",
    Admission: "bg-purple-100 text-purple-800 border-purple-200",
    Notice: "bg-yellow-100 text-yellow-800 border-yellow-200",
    Reply: "bg-indigo-100 text-indigo-800 border-indigo-200",
    Evidence: "bg-orange-100 text-orange-800 border-orange-200",
    Arguments: "bg-red-100 text-red-800 border-red-200",
    Reserved: "bg-gray-100 text-gray-800 border-gray-200",
    Disposed: "bg-green-100 text-green-800 border-green-200",
  };

  return (
    colors[stage as keyof typeof colors] ||
    "bg-gray-100 text-gray-800 border-gray-200"
  );
};

export default CauseTable;
